/**
 * comprobanteWorkbook.js
 * Generic professional Excel builder for all comprobante-type modules:
 * BoletaVenta, NotaVenta, Cotizacion, NotaCredito, NotaDebito.
 *
 * Usage:
 *   import { exportComprobanteWorkbook, COMPROBANTE_CONFIG } from '../utils/excel/comprobanteWorkbook';
 *   await exportComprobanteWorkbook({ records, clientes, searchText, usuario, config: COMPROBANTE_CONFIG.BOLETA });
 */

import { saveAs } from 'file-saver';

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const COMPROBANTE_CONFIG = {
  BOLETA: {
    titulo: 'REPORTE PROFESIONAL - BOLETAS DE VENTA',
    sheetResumen: 'Resumen Boletas',
    sheetDatos: 'Datos Boletas',
    tableName: 'tblBoletas',
    fileName: 'Boletas',
    hasCpe: true,
    labelDocumento: 'Boleta'
  },
  NOTA_VENTA: {
    titulo: 'REPORTE PROFESIONAL - NOTAS DE VENTA',
    sheetResumen: 'Resumen Notas Venta',
    sheetDatos: 'Datos Notas Venta',
    tableName: 'tblNotasVenta',
    fileName: 'NotasVenta',
    hasCpe: false,
    labelDocumento: 'Nota de Venta'
  },
  COTIZACION: {
    titulo: 'REPORTE PROFESIONAL - COTIZACIONES',
    sheetResumen: 'Resumen Cotizaciones',
    sheetDatos: 'Datos Cotizaciones',
    tableName: 'tblCotizaciones',
    fileName: 'Cotizaciones',
    hasCpe: false,
    labelDocumento: 'Cotización'
  },
  NOTA_CREDITO: {
    titulo: 'REPORTE PROFESIONAL - NOTAS DE CRÉDITO',
    sheetResumen: 'Resumen Notas Crédito',
    sheetDatos: 'Datos Notas Crédito',
    tableName: 'tblNotasCredito',
    fileName: 'NotasCredito',
    hasCpe: true,
    labelDocumento: 'Nota de Crédito'
  },
  NOTA_DEBITO: {
    titulo: 'REPORTE PROFESIONAL - NOTAS DE DÉBITO',
    sheetResumen: 'Resumen Notas Débito',
    sheetDatos: 'Datos Notas Débito',
    tableName: 'tblNotasDebito',
    fileName: 'NotasDebito',
    hasCpe: true,
    labelDocumento: 'Nota de Débito'
  }
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function normalizeText(value) {
  return String(value ?? '').trim();
}

function toDate(value) {
  if (!value) return null;
  const text = normalizeText(value);
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateText(value) {
  const date = toDate(value);
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function resolveEstadoComercial(fechaValidez) {
  if (!fechaValidez) return '';
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const venc = toDate(fechaValidez);
  if (!venc) return '';
  venc.setHours(0, 0, 0, 0);
  if (venc < hoy) return 'VENCIDA';
  if ((venc - hoy) / 86400000 <= 3) return 'POR VENCER';
  return 'VIGENTE';
}

const CPE_LABELS = {
  PENDIENTE: 'Pendiente', PROCESANDO: 'Procesando', PREPARADO: 'Preparado',
  ENVIADO: 'Enviado a SUNAT', ACEPTADO: 'Aceptado', RECHAZADO: 'Rechazado', ANULADO: 'Anulado'
};

function resolveCpeLabel(estadoCpe) {
  const code = normalizeText(estadoCpe).toUpperCase();
  return CPE_LABELS[code] || code || '';
}

function resolveClienteInfo(record, clientes) {
  const cliente = clientes.find((c) => Number(c.id) === Number(record.cliente_id));
  if (!cliente) return { nombre: record.cliente_id ? `ID ${record.cliente_id}` : 'Sin cliente', documento: '' };
  return {
    nombre: cliente.razon_social || cliente.nombre || cliente.nombres || 'Cliente',
    documento: cliente.numero_documento || ''
  };
}

function moneyValue(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function buildRows(records, clientes) {
  return records.map((r, index) => {
    const cliente = resolveClienteInfo(r, clientes);
    const total = moneyValue(r.total);
    const tipoCambio = moneyValue(r.tipo_cambio || 1);
    const moneda = normalizeText(r.moneda || 'PEN') || 'PEN';
    const estadoComercial = resolveEstadoComercial(r.fecha_validez);
    const estadoCpe = resolveCpeLabel(r.estado_cpe);
    const anulada = ['ANULADO', 'RECHAZADO'].includes(r.estado_cpe)
      || ['ANULADA', 'RECHAZADA'].includes(r.estado)
      || Number(r.estado_registro) === 0;

    return {
      item: index + 1,
      id: r.id,
      documento: `${normalizeText(r.serie)}-${String(Number(r.numero || 0)).padStart(8, '0')}`,
      serie: normalizeText(r.serie),
      numero: Number(r.numero || 0),
      cliente: cliente.nombre,
      clienteDocumento: cliente.documento,
      fechaEmision: toDate(r.fecha_emision),
      fechaEmisionText: formatDateText(r.fecha_emision),
      fechaVencimiento: toDate(r.fecha_validez),
      fechaVencimientoText: formatDateText(r.fecha_validez),
      estadoComercial,
      estadoCpe,
      estadoGeneral: anulada ? 'ANULADA' : (estadoCpe || estadoComercial || normalizeText(r.estado) || 'PENDIENTE'),
      moneda,
      tipoCambio,
      subtotal: anulada ? 0 : moneyValue(r.subtotal),
      igv: anulada ? 0 : moneyValue(r.total_igv),
      total: anulada ? 0 : total,
      totalPen: anulada ? 0 : (moneda === 'USD' ? total * (tipoCambio || 1) : total),
      observaciones: normalizeText(r.observaciones)
    };
  });
}

// ── Shared style helpers ──────────────────────────────────────────────────────

function setThinBorder(cell, color = 'FFD0D7DE') {
  cell.border = {
    top: { style: 'thin', color: { argb: color } },
    left: { style: 'thin', color: { argb: color } },
    bottom: { style: 'thin', color: { argb: color } },
    right: { style: 'thin', color: { argb: color } }
  };
}

function styleMergedBlock(worksheet, range, fill, fontColor = 'FFFFFFFF', fontSize = 11) {
  worksheet.mergeCells(range);
  const cell = worksheet.getCell(range.split(':')[0]);
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  cell.font = { bold: true, color: { argb: fontColor }, size: fontSize };
  cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  setThinBorder(cell, fill);
  return cell;
}

function addKpi(worksheet, labelRange, valueRange, label, value, fill) {
  const labelCell = styleMergedBlock(worksheet, labelRange, fill, 'FFFFFFFF', 10);
  labelCell.value = label;
  const valueCell = styleMergedBlock(worksheet, valueRange, 'FFF7FAFC', 'FF123247', 16);
  valueCell.value = value;
  valueCell.font = { bold: true, size: 16, color: { argb: 'FF123247' } };
  setThinBorder(valueCell, 'FFD7E3EC');
}

function colorizeStateCell(cell, value) {
  const state = normalizeText(value).toUpperCase();
  const palette = {
    VIGENTE: { fill: 'FFE8F5E9', font: 'FF1B5E20' },
    'POR VENCER': { fill: 'FFFFF4E5', font: 'FF9A6700' },
    VENCIDA: { fill: 'FFFDECEC', font: 'FFB42318' },
    ACEPTADO: { fill: 'FFE8F5E9', font: 'FF1B5E20' },
    'ENVIADO A SUNAT': { fill: 'FFFFF4E5', font: 'FF9A6700' },
    ENVIADO: { fill: 'FFFFF4E5', font: 'FF9A6700' },
    RECHAZADO: { fill: 'FFFDECEC', font: 'FFB42318' },
    ANULADA: { fill: 'FFF3F4F6', font: 'FF344054' },
    ANULADO: { fill: 'FFF3F4F6', font: 'FF344054' },
    PENDIENTE: { fill: 'FFEFF8FF', font: 'FF175CD3' },
    PROCESANDO: { fill: 'FFEFF8FF', font: 'FF175CD3' }
  }[state];
  if (!palette) return;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: palette.fill } };
  cell.font = { bold: true, color: { argb: palette.font } };
}

function styleHeaderRow(row) {
  row.height = 24;
  row.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF123247' } };
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    setThinBorder(cell, 'FF123247');
  });
}

function styleBodyRow(row, rowIndex, stateColumnIndexes = []) {
  row.eachCell((cell) => {
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.fill = {
      type: 'pattern', pattern: 'solid',
      fgColor: { argb: rowIndex % 2 === 0 ? 'FFF8FAFC' : 'FFFFFFFF' }
    };
    setThinBorder(cell);
  });
  stateColumnIndexes.forEach((ci) => {
    colorizeStateCell(row.getCell(ci), row.getCell(ci).value);
  });
}

// ── Sheet builders ────────────────────────────────────────────────────────────

function createResumenSheet(workbook, rows, config, searchText, usuario) {
  const hasCpe = config.hasCpe;
  // Column count: 9 without CPE, 10 with CPE
  const totalCols = hasCpe ? 10 : 9;
  const lastCol = String.fromCharCode(64 + totalCols); // A=65

  const worksheet = workbook.addWorksheet(config.sheetResumen, {
    views: [{ state: 'frozen', ySplit: 8 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });
  worksheet.properties.defaultRowHeight = 21;

  const colWidths = hasCpe
    ? [{ width: 7 }, { width: 18 }, { width: 18 }, { width: 16 }, { width: 16 }, { width: 16 }, { width: 18 }, { width: 12 }, { width: 12 }, { width: 16 }]
    : [{ width: 7 }, { width: 18 }, { width: 18 }, { width: 16 }, { width: 16 }, { width: 16 }, { width: 12 }, { width: 12 }, { width: 16 }];
  worksheet.columns = colWidths;

  // Row 1 — Title
  worksheet.mergeCells(`A1:${lastCol}1`);
  const titleCell = worksheet.getCell('A1');
  titleCell.value = config.titulo;
  titleCell.font = { bold: true, size: 18, color: { argb: 'FFFFFFFF' } };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF123247' } };
  worksheet.getRow(1).height = 28;

  // Row 2 — Timestamp
  worksheet.mergeCells(`A2:${lastCol}2`);
  const tsCell = worksheet.getCell('A2');
  tsCell.value = `Generado el ${new Date().toLocaleString('es-PE')}${usuario ? ` | Usuario: ${usuario}` : ''}`;
  tsCell.font = { italic: true, size: 10, color: { argb: 'FF475467' } };
  tsCell.alignment = { horizontal: 'center' };
  worksheet.getRow(2).height = 20;

  // Row 3 — Filter
  worksheet.mergeCells(`A3:${lastCol}3`);
  const filterCell = worksheet.getCell('A3');
  filterCell.value = `Filtro aplicado: ${searchText || 'Todos los registros visibles'}`;
  filterCell.font = { size: 10, color: { argb: 'FF344054' } };
  filterCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7FA' } };
  filterCell.alignment = { horizontal: 'left' };

  // KPIs (rows 5-7)
  const totalDocumentos = rows.length;
  const totalImporte = rows.reduce((a, r) => a + r.totalPen, 0);
  const vigentes = rows.filter((r) => r.estadoComercial === 'VIGENTE').length;
  const porVencer = rows.filter((r) => r.estadoComercial === 'POR VENCER').length;
  const vencidas = rows.filter((r) => r.estadoComercial === 'VENCIDA').length;
  const aceptadas = hasCpe ? rows.filter((r) => r.estadoCpe === 'Aceptado').length : 0;

  addKpi(worksheet, 'A5:B5', 'A6:B7', 'Documentos', totalDocumentos, 'FF175CD3');
  addKpi(worksheet, 'D5:E5', 'D6:E7', 'Importe total (PEN)', totalImporte, 'FF039855');
  worksheet.getCell('D6').numFmt = '#,##0.00';

  if (hasCpe) {
    addKpi(worksheet, 'G5:H5', 'G6:H7', 'Aceptados SUNAT', aceptadas, 'FF7A5AF8');
    addKpi(worksheet, 'I5:J5', 'I6:J7', 'Vencidos', vencidas, 'FFDC2626');
  } else {
    addKpi(worksheet, 'G5:H5', 'G6:H7', 'Vigentes', vigentes, 'FF027A48');
    addKpi(worksheet, 'I5', 'I6:I7', 'Vencidos', vencidas, 'FFDC2626');
  }
  addKpi(worksheet, 'A8:B8', 'A9:B10', 'Vigentes', vigentes, 'FF027A48');
  addKpi(worksheet, 'D8:E8', 'D9:E10', 'Por vencer', porVencer, 'FFB54708');

  // Table header (row 12)
  const HEADER_ROW = 12;
  const headers = hasCpe
    ? ['Documento', 'Cliente', 'Doc. cliente', 'Emisión', 'Vence', 'Estado comercial', 'Estado CPE', 'Moneda', 'T/C', 'Total']
    : ['Documento', 'Cliente', 'Doc. cliente', 'Emisión', 'Vence', 'Estado comercial', 'Moneda', 'T/C', 'Total'];
  worksheet.getRow(HEADER_ROW).values = headers;
  styleHeaderRow(worksheet.getRow(HEADER_ROW));

  // Data rows
  rows.forEach((row, index) => {
    const excelRow = worksheet.getRow(HEADER_ROW + 1 + index);
    const values = hasCpe
      ? [row.documento, row.cliente, row.clienteDocumento, row.fechaEmision || row.fechaEmisionText, row.fechaVencimiento || row.fechaVencimientoText, row.estadoComercial, row.estadoCpe || row.estadoGeneral, row.moneda, row.tipoCambio, row.total]
      : [row.documento, row.cliente, row.clienteDocumento, row.fechaEmision || row.fechaEmisionText, row.fechaVencimiento || row.fechaVencimientoText, row.estadoComercial, row.moneda, row.tipoCambio, row.total];
    excelRow.values = values;

    const dateCol4 = 4;
    const dateCol5 = 5;
    const stateCol6 = 6;
    const cpeCol7 = hasCpe ? 7 : null;
    const monedaCol = hasCpe ? 8 : 7;
    const tcCol = hasCpe ? 9 : 8;
    const totalCol = hasCpe ? 10 : 9;

    excelRow.getCell(dateCol4).numFmt = 'dd/mm/yyyy';
    excelRow.getCell(dateCol5).numFmt = 'dd/mm/yyyy';
    excelRow.getCell(tcCol).numFmt = '#,##0.0000';
    excelRow.getCell(totalCol).numFmt = '#,##0.00';
    excelRow.height = 20;

    const stateCols = cpeCol7 ? [stateCol6, cpeCol7] : [stateCol6];
    styleBodyRow(excelRow, index, stateCols);

    excelRow.getCell(monedaCol).alignment = { horizontal: 'center', vertical: 'middle' };
    excelRow.getCell(tcCol).alignment = { horizontal: 'right', vertical: 'middle' };
    excelRow.getCell(totalCol).alignment = { horizontal: 'right', vertical: 'middle' };
  });

  // AutoFilter
  const lastDataRow = Math.max(HEADER_ROW, HEADER_ROW + rows.length);
  worksheet.autoFilter = {
    from: { row: HEADER_ROW, column: 1 },
    to: { row: HEADER_ROW, column: totalCols }
  };

  // Total row
  const totalRow = worksheet.getRow(lastDataRow + 1);
  const totalCol2 = hasCpe ? 10 : 9;
  totalRow.getCell(1).value = 'TOTAL GENERAL';
  totalRow.getCell(totalCol2).value = totalImporte;
  totalRow.getCell(totalCol2).numFmt = '#,##0.00';
  totalRow.height = 22;
  totalRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FF123247' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAF2F8' } };
    setThinBorder(cell, 'FFB9C8D6');
  });
}

function createDatosSheet(workbook, rows, config) {
  const hasCpe = config.hasCpe;
  const worksheet = workbook.addWorksheet(config.sheetDatos, {
    views: [{ state: 'frozen', ySplit: 1 }]
  });

  const tableRows = rows.map((r) => {
    const base = [r.item, r.id, r.documento, r.serie, r.numero, r.cliente, r.clienteDocumento, r.fechaEmisionText, r.fechaVencimientoText, r.estadoComercial];
    return hasCpe
      ? [...base, r.estadoCpe, r.estadoGeneral, r.moneda, r.tipoCambio, r.subtotal, r.igv, r.total, r.totalPen, r.observaciones]
      : [...base, r.estadoGeneral, r.moneda, r.tipoCambio, r.subtotal, r.igv, r.total, r.totalPen, r.observaciones];
  });

  const columns = hasCpe
    ? [
        { name: 'Item', totalsRowLabel: 'Totales' }, { name: 'ID' }, { name: 'Documento' },
        { name: 'Serie' }, { name: 'Numero' }, { name: 'Cliente' }, { name: 'DocCliente' },
        { name: 'FechaEmision' }, { name: 'FechaVencimiento' }, { name: 'EstadoComercial' },
        { name: 'EstadoCPE' }, { name: 'EstadoGeneral' }, { name: 'Moneda' },
        { name: 'TipoCambio' }, { name: 'Subtotal', totalsRowFunction: 'sum' },
        { name: 'IGV', totalsRowFunction: 'sum' }, { name: 'Total', totalsRowFunction: 'sum' },
        { name: 'TotalPEN', totalsRowFunction: 'sum' }, { name: 'Observaciones' }
      ]
    : [
        { name: 'Item', totalsRowLabel: 'Totales' }, { name: 'ID' }, { name: 'Documento' },
        { name: 'Serie' }, { name: 'Numero' }, { name: 'Cliente' }, { name: 'DocCliente' },
        { name: 'FechaEmision' }, { name: 'FechaVencimiento' }, { name: 'EstadoComercial' },
        { name: 'EstadoGeneral' }, { name: 'Moneda' }, { name: 'TipoCambio' },
        { name: 'Subtotal', totalsRowFunction: 'sum' }, { name: 'IGV', totalsRowFunction: 'sum' },
        { name: 'Total', totalsRowFunction: 'sum' }, { name: 'TotalPEN', totalsRowFunction: 'sum' },
        { name: 'Observaciones' }
      ];

  worksheet.addTable({
    name: config.tableName,
    ref: 'A1',
    headerRow: true,
    totalsRow: true,
    style: { theme: 'TableStyleMedium2', showRowStripes: true },
    columns,
    rows: tableRows
  });

  worksheet.columns = hasCpe
    ? [{ width: 8 }, { width: 8 }, { width: 18 }, { width: 10 }, { width: 10 }, { width: 30 }, { width: 18 }, { width: 14 }, { width: 14 }, { width: 16 }, { width: 18 }, { width: 18 }, { width: 10 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 16 }, { width: 26 }]
    : [{ width: 8 }, { width: 8 }, { width: 18 }, { width: 10 }, { width: 10 }, { width: 30 }, { width: 18 }, { width: 14 }, { width: 14 }, { width: 16 }, { width: 18 }, { width: 10 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 16 }, { width: 26 }];

  worksheet.getRow(1).font = { bold: true };
  const numColStart = hasCpe ? 14 : 13;
  for (let ri = 2; ri <= rows.length + 2; ri += 1) {
    const tcCell = worksheet.getCell(`${String.fromCharCode(64 + numColStart)}${ri}`);
    tcCell.numFmt = '#,##0.0000';
    for (let ci = numColStart + 1; ci <= numColStart + 4; ci += 1) {
      worksheet.getCell(`${String.fromCharCode(64 + ci)}${ri}`).numFmt = '#,##0.00';
    }
  }

  worksheet.getCell(`A${rows.length + 4}`).value = 'Base de datos lista para filtros avanzados y tabla dinámica en Excel.';
  worksheet.getCell(`A${rows.length + 4}`).font = { italic: true, color: { argb: 'FF667085' } };
}

// ── Public export ──────────────────────────────────────────────────────────────

/**
 * @param {Object} options
 * @param {Array}  options.records     - Filtered records array from the module
 * @param {Array}  options.clientes    - Full clientes array for name resolution
 * @param {string} options.searchText  - Current search text (for filter annotation)
 * @param {string} options.usuario     - Logged-in username
 * @param {Object} options.config      - One of COMPROBANTE_CONFIG values
 */
export async function exportComprobanteWorkbook({ records, clientes, searchText, usuario, config }) {
  const ExcelJSModule = await import('exceljs');
  const ExcelJS = ExcelJSModule.default || ExcelJSModule;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'InsightSoft';
  workbook.created = new Date();

  const rows = buildRows(records, clientes);
  createResumenSheet(workbook, rows, config, searchText, usuario);
  createDatosSheet(workbook, rows, config);

  const buffer = await workbook.xlsx.writeBuffer();
  const timestamp = new Date().toISOString().slice(0, 10);
  saveAs(new Blob([buffer], { type: EXCEL_MIME }), `${config.fileName}-${timestamp}.xlsx`);
}
